/**
 * Deterministic Tailwind class lookups for application state UI.
 * Use full literal strings so Tailwind's content scanner includes only these utilities.
 * Do not build class names with string concatenation or template literals.
 *
 * Colours are design-system roles (tones), so they follow the theme.
 */

import { TONE_SOFT, TONE_TEXT, type Tone } from '../../design-system/ui/tone';

export type StateButtonColor = 'purple' | 'green' | 'red' | 'emerald' | 'blue';

/** The status tone each stored state colour name stands for (MEANING.md): in progress is info, progress that helps is positive, a rejection is attention. */
const COLOR_TONE: Record<StateButtonColor, Tone> = {
  purple: 'info',
  green: 'positive',
  red: 'attention',
  emerald: 'positive',
  blue: 'info',
};

/**
 * Button background/border classes for application state (ellipsis / state chip). The keys are the
 * state colour names stored with each state; the colours are status tones from the design system.
 */
export const STATE_BUTTON_COLOR_CLASSES: Record<StateButtonColor | 'default', string> = {
  purple: `border border-transparent ${TONE_SOFT[COLOR_TONE.purple]}`,
  green: `border border-transparent ${TONE_SOFT[COLOR_TONE.green]}`,
  red: `border border-transparent ${TONE_SOFT[COLOR_TONE.red]}`,
  emerald: `border border-transparent ${TONE_SOFT[COLOR_TONE.emerald]}`,
  blue: `border border-transparent ${TONE_SOFT[COLOR_TONE.blue]}`,
  default: 'border bg-jc-surface/80 border-jc-border',
};

/** Icon text color classes for application state. */
export const STATE_ICON_COLOR_CLASSES: Record<StateButtonColor | 'default', string> = {
  purple: TONE_TEXT[COLOR_TONE.purple],
  green: TONE_TEXT[COLOR_TONE.green],
  red: TONE_TEXT[COLOR_TONE.red],
  emerald: TONE_TEXT[COLOR_TONE.emerald],
  blue: TONE_TEXT[COLOR_TONE.blue],
  default: 'text-jc-ink-secondary',
};

export type ApplicationStateLabelValue = 'interviewing' | 'offer' | 'rejected' | 'hired' | 'applied' | '';

/** Select/label border and text color per application state. */
export const STATE_LABEL_CLASSES: Record<ApplicationStateLabelValue, string> = {
  interviewing: `${TONE_TEXT.info} border-jc-border`,
  offer: `${TONE_TEXT.positive} border-jc-border`,
  rejected: `${TONE_TEXT.attention} border-jc-border`,
  hired: `${TONE_TEXT.positive} border-jc-border`,
  applied: `${TONE_TEXT.positive} border-jc-border`,
  '': 'text-jc-ink-secondary border-jc-border',
};

export function getStateLabelClass(stateVal: string | null | undefined, isApplied: boolean): string {
  if (stateVal === 'interviewing' || stateVal === 'offer' || stateVal === 'rejected' || stateVal === 'hired') {
    return STATE_LABEL_CLASSES[stateVal as keyof typeof STATE_LABEL_CLASSES];
  }
  if (isApplied) return STATE_LABEL_CLASSES.applied;
  return STATE_LABEL_CLASSES[''];
}

/** Hit card / compact menu trigger surface (when a state colour is active). */
export function getHitStateButtonSurfaceClass(color: string): string {
  const key = color as keyof typeof STATE_BUTTON_COLOR_CLASSES;
  return STATE_BUTTON_COLOR_CLASSES[key] ?? STATE_BUTTON_COLOR_CLASSES.default;
}

/** Icon colour for state glyph on hit menu button. */
export function getHitStateIconClass(color: string): string {
  const key = color as keyof typeof STATE_ICON_COLOR_CLASSES;
  return STATE_ICON_COLOR_CLASSES[key] ?? STATE_ICON_COLOR_CLASSES.default;
}
